import { useRef, useState } from 'react'
import { DecisionBox, DecisionBoxHandle } from '../components/DecisionBox'
import { WarningFormBox } from '../components/WarningFormBox'
import { BottomActionBar } from '../components/BottomActionBar'
import type { ConversationState, ConversationActions } from '../hooks/useConversation'
import type { E3Data, NavigationActions } from '../hooks/useNavigation'
import { Arrangement } from '../types/arrangement'
import { useSwipeBack } from '../hooks/useSwipeBack'

const SPRING = 'cubic-bezier(0.25, 1, 0.2, 1)'

interface Screen3Positions {
  titleTop: number
  decisionTop: number
  decisionHeightPct: number
  noteBarTopDOM: number
  noteBarScale: number
  actionBarBottom: number
}

interface Props {
  positions: Screen3Positions
  viewportWidth: number
  fontSize: number
  onFontSizeChange: (action: 'increase' | 'decrease') => void
  conv: ConversationState & ConversationActions
  e3Data: E3Data
  setE3Body: (v: string) => void
  onGoToE2: () => void
  onGoToE1: (actionType: 'NOTE' | 'WARNING' | 'REPRIMAND', formData?: { imie: string; nazwisko: string; stanowisko: string }) => void
  setE3Arrangements: (v: string) => void
  addE3Arrangement: NavigationActions['addE3Arrangement']
  updateE3Arrangement: NavigationActions['updateE3Arrangement']
  removeE3Arrangement: NavigationActions['removeE3Arrangement']
}

export function Screen3({
  positions, fontSize,
  conv, e3Data, setE3Body, onGoToE1, setE3Arrangements,
  addE3Arrangement, updateE3Arrangement, removeE3Arrangement,
}: Props) {
  const decisionBoxRef   = useRef<DecisionBoxHandle>(null)
  const speechRef        = useRef<any>(null)
  const warningTypeRef   = useRef<'upomnienie' | 'nagana'>('upomnienie')
  const [showWarningForm, setShowWarningForm] = useState(false)
  const [isRecording, setIsRecording]         = useState(false)
  
  // Śledzenie focus i pozycji kursora — zarówno treść notatki jak i ustalenia
  const focusedArrangementIdRef = useRef<string | null>(null)
  const cursorPositionRef       = useRef<number>(0)
  const focusedFieldRef         = useRef<'content' | 'arrangement' | null>(null)
  const contentCursorRef        = useRef<number>(0)
  const isHoldConfirmedRef      = useRef<boolean>(false)  // true gdy hold > 350ms

  const handleWarningSubmit = (formData?: { imie: string; nazwisko: string; stanowisko: string }) => {
    const actionType = warningTypeRef.current === 'nagana' ? 'REPRIMAND' : 'WARNING'
    setShowWarningForm(false)
    onGoToE1(actionType, formData)
  }

  const currentDictationRef = useRef('')
  
  // Dźwięk ostrzegawczy (taki sam jak na E1)
  function _t(freq: number, vol: number, durS: number) {
    try {
      const ctx  = new (window.AudioContext || (window as any).webkitAudioContext)()
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(ctx.destination)
      osc.frequency.value = freq; osc.type = 'sine'
      gain.gain.setValueAtTime(vol, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durS)
      osc.start(); osc.stop(ctx.currentTime + durS)
      osc.onended = () => ctx.close()
    } catch (_) {}
  }
  const warningSound = () => {
    _t(880, 0.15, 0.08)
    setTimeout(() => { _t(660, 0.25, 0.08); setTimeout(() => _t(440, 0.18, 0.1), 100) }, 150)
  }

  // Handler dla zmiany focus w ustaleniach
  const handleArrangementFocusChange = (id: string | null, cursorPos: number) => {
    focusedArrangementIdRef.current = id
    cursorPositionRef.current = cursorPos
    focusedFieldRef.current = id ? 'arrangement' : null
    console.log('[Screen3] handleArrangementFocusChange - id:', id, 'cursorPos:', cursorPos, 'focusedField:', focusedFieldRef.current)
  }

  // Handler dla zmiany focus w treści notatki
  const handleContentFocusChange = (cursorPos: number) => {
    focusedFieldRef.current = 'content'
    contentCursorRef.current = cursorPos
    focusedArrangementIdRef.current = null
  }

  // natychmiastowy start SR w gesture context (iOS Safari wymaga tego synchronicznie)
  const handleMicDown = () => {
    console.log('[Screen3] handleMicDown - focusedFieldRef.current:', focusedFieldRef.current, 'focusedArrangementIdRef.current:', focusedArrangementIdRef.current, 'cursorPositionRef.current:', cursorPositionRef.current)
    // Brak focusu = mikrofon zablokowany: wibracja + dźwięk ostrzegawczy
    if (!focusedFieldRef.current) {
      console.log('[Screen3] handleMicDown - NO FOCUS - blocking mic')
      if (navigator.vibrate) navigator.vibrate([60, 40, 60])
      warningSound()
      return
    }
    isHoldConfirmedRef.current = false
    currentDictationRef.current = ''
    if (speechRef.current) { try { speechRef.current.stop() } catch(_){} speechRef.current = null }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) return
    const sr = new SR()
    sr.lang = 'pl-PL'; sr.continuous = true; sr.interimResults = false
    sr.onresult = (e: any) => {
      const results = Array.from(e.results as any[])
      const text = results.map((r: any) => r[0].transcript).join('')
      console.log('[Screen3] SR onresult - text:', text, 'currentDictationRef.current:', currentDictationRef.current)
      const sep = currentDictationRef.current.length > 0 && !currentDictationRef.current.endsWith(' ') ? ' ' : ''
      currentDictationRef.current += sep + text
      console.log('[Screen3] SR onresult - updated currentDictationRef.current:', currentDictationRef.current)
    }
    sr.onerror = (e: any) => {
      console.log('[Screen3] SR onerror - error:', e.error, 'message:', e.message)
    }
    sr.onend = () => { 
      console.log('[Screen3] SR onend - currentDictationRef.current:', currentDictationRef.current)
      setIsRecording(false)
      
      // Dodaj tekst gdy SR faktycznie skończy pracę
      const text = currentDictationRef.current.trim()
      console.log('[Screen3] SR onend - adding text:', text)
      if (text && focusedFieldRef.current === 'content') {
        const cursorPos = contentCursorRef.current
        const currentBody = e3Data.body
        const before = currentBody.slice(0, cursorPos)
        const after = currentBody.slice(cursorPos)
        const prefix = (before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n')) ? ' ' : ''
        const newBody = before + prefix + text + after
        setE3Body(newBody)
        contentCursorRef.current = cursorPos + prefix.length + text.length
        console.log('[Screen3] SR onend - Inserted text at cursor position', cursorPos, 'in content')
        currentDictationRef.current = ''
      } else if (text && focusedFieldRef.current === 'arrangement' && focusedArrangementIdRef.current) {
        const arrangement = e3Data.arrangementList.find(a => a.id === focusedArrangementIdRef.current)
        if (arrangement) {
          const currentText = arrangement.text
          const cursorPos = cursorPositionRef.current
          const before = currentText.slice(0, cursorPos)
          const after = currentText.slice(cursorPos)
          const prefix = (before.length > 0 && !before.endsWith(' ')) ? ' ' : ''
          const newText = before + prefix + text + after
          updateE3Arrangement(focusedArrangementIdRef.current, { text: newText })
          console.log('[Screen3] SR onend - Inserted text at cursor position', cursorPos, 'in arrangement', focusedArrangementIdRef.current)
        } else {
          // Kafelek nie istnieje w e3Data (pusty) - dodaj nowe ustalenie
          const newItem: Arrangement = { id: crypto.randomUUID(), text: text, done: false }
          addE3Arrangement(newItem)
          console.log('[Screen3] SR onend - Added new arrangement from dictation:', text)
        }
        currentDictationRef.current = ''
      } else {
        console.log('[Screen3] SR onend - No text to add or no focused field')
        currentDictationRef.current = ''
      }
    }
    try { sr.start() } catch(_) { return }
    speechRef.current = sr
  }

  // Po 350ms potwierdzono hold — aktywuj UI nagrywania
  const handleMicHoldStart = () => {
    if (!focusedFieldRef.current) return  // blokada bez SR (już obsłużone w handleMicDown)
    if (!speechRef.current) return        // SR nie został uruchomiony (brak SR API)
    isHoldConfirmedRef.current = true
    setIsRecording(true)
  }

  const handleMicHoldEnd = () => {
    if (!isHoldConfirmedRef.current) {
      // Tap (< 350ms) — odrzucamy tekst, tylko zatrzymujemy SR
      if (speechRef.current) { try { speechRef.current.stop() } catch(_){} speechRef.current = null }
      currentDictationRef.current = ''
      return
    }
    // Zatrzymaj SR - tekst zostanie dodany w sr.onend
    if (speechRef.current) {
      try { speechRef.current.stop() } catch(_){} speechRef.current = null
    }
  }

  // Czerwony przycisk — tap/hold → upomnienie/nagana
  const [redPushed, setRedPushed]   = useState(false)
  const [redLong, setRedLong]       = useState(false)
  const redTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const redFiredRef = useRef(false)

  const handleRedDown = () => {
    setRedPushed(true); redFiredRef.current = false
    redTimerRef.current = setTimeout(() => {
      redFiredRef.current = true; setRedLong(true)
      warningTypeRef.current = 'nagana'; setShowWarningForm(true)
    }, 3000)
  }
  const handleRedUp = () => {
    if (redTimerRef.current) clearTimeout(redTimerRef.current)
    setRedPushed(false); setRedLong(false)
    if (!redFiredRef.current) { warningTypeRef.current = 'upomnienie'; setShowWarningForm(true) }
    redFiredRef.current = false
  }
  const handleRedLeave = () => {
    if (redTimerRef.current) clearTimeout(redTimerRef.current)
    setRedPushed(false); setRedLong(false); redFiredRef.current = false
  }

  // Rozmiar czerwonego przycisku - taki sam jak mikrofon (67px) - stały rozmiar
  const redSize = 67

  // Swipe left = powrót do E1 (zapis notatki), tak jak przycisk potwierdzenia.
  const swipeBack = useSwipeBack(() => onGoToE1('NOTE'))

  // DEBUG: Przykładowe dane jeśli e3Data jest puste
  const displayHeader = e3Data.header || 'Robert, wt. 19.05.2026'
  const displayBody = e3Data.body || 'Treść notatki, treść notatki, treść notatki, treść notatki, treść notatki, treść notatki, treść notatki.'
  // Zawsze n+1 kafelków: wszystkie z E2 + 1 pusty z placeholderem (tylko jeśli ostatni nie jest pusty)
  const lastArrangement = e3Data.arrangementList[e3Data.arrangementList.length - 1]
  const needsEmptyTile = !lastArrangement || lastArrangement.text.trim() !== ''
  const displayArrangements = [
    ...e3Data.arrangementList,
    ...(needsEmptyTile ? [{ id: `new-${Date.now()}`, text: '', done: false }] : []) // Pusty tylko jeśli potrzeba
  ]

  return (
    <div className="relative w-full h-full" onTouchStart={swipeBack.onTouchStart} onTouchEnd={swipeBack.onTouchEnd}>
      <div style={{
        position: 'absolute', top: `${positions.titleTop}%`, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#D9D9D9', textAlign: 'center',
        fontFamily: '"Inter", sans-serif', fontSize: 'clamp(24px, 4vw, 32px)',
        fontWeight: 200, letterSpacing: '3px',
        fontFeatureSettings: "'liga' off, 'clig' off", whiteSpace: 'nowrap',
      }}>Twoja Decyzja</div>

      {/* DecisionBox — poszerzony (więcej miejsca bo usunięto WarningNoteControlBar) */}
      <div style={{
        position: 'absolute', top: `${positions.decisionTop}%`,
        left: '4.8%', right: '4.8%', height: `${positions.decisionHeightPct}%`,
      }}>
        <DecisionBox
          ref={decisionBoxRef}
          fontSize={fontSize}
          fillHeight
          header={displayHeader}
          subheader="Co ustaliliście?"
          content={displayBody}
          arrangements={e3Data.arrangements}
          arrangementList={displayArrangements}
          onContentChange={setE3Body}
          onArrangementsChange={setE3Arrangements}
          onArrangementChange={updateE3Arrangement}
          onArrangementRemove={removeE3Arrangement}
          onArrangementFocusChange={handleArrangementFocusChange}
          onContentFocusChange={handleContentFocusChange}
        />
      </div>

      {/* Czerwony przycisk — upomnienie/nagana — na miejscu lewego (symetrycznie do mikrofonu) */}
      <div style={{
        position: 'absolute',
        bottom: `${positions.actionBarBottom}%`,
        left: 'calc(50% - 152px)',
        display: 'flex', justifyContent: 'flex-start',
        zIndex: 20,
      }}>
        <div
          onMouseDown={handleRedDown}
          onMouseUp={handleRedUp}
          onMouseLeave={handleRedLeave}
          onTouchStart={handleRedDown}
          onTouchEnd={handleRedUp}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: `${redSize}px`, height: `${redSize}px`,
            borderRadius: '50%',
            backdropFilter: 'blur(16.5px)', WebkitBackdropFilter: 'blur(16.5px)',
            background: redPushed
              ? 'radial-gradient(50% 50% at 50% 50%, rgba(255,0,0,0.45) 47%, rgba(153,0,0,0.15) 100%)'
              : 'radial-gradient(50% 50% at 50% 50%, rgba(255,0,0,0.21) 44%, rgba(153,0,0,0.08) 86%)',
            boxShadow: redPushed
              ? 'inset 0 1px 3px rgba(255,255,255,0.5), 0 0 25px rgba(255,0,0,0.35)'
              : 'inset 0 1px 2px rgba(255,255,255,0.3), 0 8px 32px rgba(0,0,0,0.25)',
            border: redPushed ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.08)',
            transition: `all 280ms ${SPRING}`,
            transform: redPushed ? (redLong ? 'scale(0.90)' : 'scale(0.94)') : 'scale(1)',
            outline: redLong ? '2px solid rgba(255,100,100,0.7)' : 'none',
            cursor: 'pointer', touchAction: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/* Ikona koperty */}
          <svg width={redSize * 0.38} height={redSize * 0.38} viewBox="0 0 16 16" fill="none">
            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4.5a.5.5 0 0 1-1 0V5.383l-7 4.2-7-4.2V12a1 1 0 0 0 1 1h5.5a.5.5 0 0 1 0 1H2a2 2 0 0 1-2-2V4zm1 .217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v.217z"
              fill="white" fillOpacity={redPushed ? 0.85 : 0.45} />
            <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.354-5.354 1.25 1.25a.5.5 0 0 1-.708.708L13 12.207V14a.5.5 0 0 1-1 0v-1.793l-.396.43a.5.5 0 0 1-.737-.676l1.149-1.25a.5.5 0 0 1 .738.015z"
              fill="white" fillOpacity={redPushed ? 0.95 : 0.65} />
          </svg>
        </div>
      </div>

      {/* BottomActionBar — checkmark=NOTE+E1, mic hold=dyktowanie */}
      <div style={{
        position: 'absolute', bottom: `${positions.actionBarBottom}%`,
        left: 0, right: 0, display: 'flex', justifyContent: 'center',
      }}>
        <BottomActionBar
          onConfirmTap={() => onGoToE1('NOTE')}
          onMicDown={handleMicDown}
          onMicHoldStart={handleMicHoldStart}
          onMicHoldEnd={handleMicHoldEnd}
          isRecording={isRecording}
        />
      </div>

      {showWarningForm && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
          background: 'rgba(0,0,0,0.35)',
        }}>
          <WarningFormBox
            employeeName={conv.selectedEmployee}
            warningType={warningTypeRef.current}
            onCancelClick={() => setShowWarningForm(false)}
            onConfirmSubmit={handleWarningSubmit}
          />
        </div>
      )}

    </div>
  )
}
